/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Select from 'react-select'

import { Order } from 'store/types'
import { useProvideFollowUpCareProviderMutation } from 'store/services/labOrder'
import { useLazyGetEffectiveFollowUpPhysiciansQuery } from 'store/services/labPhysician'
import Toast from 'components/atoms/Toast'
import { isOrganization } from 'helpers/followUpProvidersHelper';

type PhysicianOption = { value: string; label: string }

interface InitiateFollowUpCareModalProps {
  open: boolean
  onClose: () => void
  order: Order
  onSuccess?: () => void
}

export const InitiateFollowUpCareModal = ({
  open,
  onClose,
  order,
  onSuccess,
}: InitiateFollowUpCareModalProps) => {
  const [provideFollowUpCareProvider] = useProvideFollowUpCareProviderMutation()
  const [getFollowUpPhysicians, { data: followUpPhysiciansData, isLoading: isLoadingFollowUp }] =
    useLazyGetEffectiveFollowUpPhysiciansQuery()
  const [selectedProvider, setSelectedProvider] = useState<PhysicianOption | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const referringPhysicianNpi = order?.physician?.npi ?? order?.verifiedPhysician?.npi

  useEffect(() => {
    if (open && referringPhysicianNpi) {
      getFollowUpPhysicians({
        physicianNpi: referringPhysicianNpi,
        officeId: order?.officeId != null ? Number(order.officeId) : undefined,
      })
      setSelectedProvider(null)
    }
  }, [open, order?.officeId, referringPhysicianNpi, getFollowUpPhysicians])

  const options: PhysicianOption[] =
    followUpPhysiciansData?.items.map((item) => {
      const p = item.followUpPhysician
      const fullName = [p?.firstName, p?.middleName, p?.lastName].filter(Boolean).join(' ')
      return {
        value: String(p?.id),
        label: isOrganization(p?.providerType)
          ? p?.organizationName || fullName
          : fullName || `Physician ${item.followUpPhysicianNpi}`,
      }
    }) ?? []
  const hasOptions = options.length > 0

  const handleSubmit = async () => {
    if (!selectedProvider) return
    setSubmitting(true)
    try {
      await provideFollowUpCareProvider({
        orderId: Number(order.id),
        physicianId: Number(selectedProvider.value),
      }).unwrap()
      Toast({ type: 'success', label: 'Follow-up care started.' })
      onClose()
      onSuccess?.()
    } catch {
      Toast({ type: 'error', label: 'Failed to start follow-up care.' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal show={open} onHide={onClose} centered>
      <Modal.Header>
        <Modal.Title>
          <i className="fas fa-user-nurse" css={{ marginRight: '0.5rem' }} />
          Initiate Follow-Up Care
        </Modal.Title>
        <button type="button" className="close" aria-label="Close" onClick={onClose}>
          <span aria-hidden="true">&times;</span>
        </button>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>Follow-up care provider</Form.Label>
          <Select
            className="basic-single"
            classNamePrefix="select"
            isClearable
            isSearchable
            placeholder={
              !referringPhysicianNpi
                ? 'No referring physician'
                : !hasOptions
                  ? 'No follow-up providers for this physician'
                  : 'Select provider...'
            }
            options={options}
            value={selectedProvider}
            onChange={(option) => setSelectedProvider(option)}
            isDisabled={!referringPhysicianNpi || !hasOptions}
            isLoading={isLoadingFollowUp}
            styles={{
              control: (base) => ({ ...base, minHeight: 38 }),
            }}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!selectedProvider || submitting}>
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
